/* eslint-disable @typescript-eslint/indent */
import React, { Dispatch, SetStateAction, useContext, useMemo } from 'react';
import ButtonPrimary from '@components/common/ButtonPrimary';
import { SELECT_CALENDAR_PRODUCT_FILTER } from '@constants/amplitudeEvents';
import {
  AMPLITUDE_EXPERIMENT_CONTROL_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME_2,
} from '@constants/amplitudeExperiments';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import { store } from '@context/store';
import HotelDetails from '@customTypes/hotel-details';
import Product from '@customTypes/product';
import { ProductDetails } from '@customTypes/product-details';
import SearchOption from '@customTypes/search-option';
import { dlpSmartCalendarProductFilterApplied } from '@events/DLP';
import { useEvents } from '@events/EventsProvider';
import { getMonthAndDay } from '@helpers/date';
import Button from '@solar/common/Button/ButtonV2';
import { format } from 'date-fns';

type Props = {
  date: Date | null;
  handleConfirmClick: () => void;
  hotel: HotelDetails;
  onProductTypeChange: (selectedOption: SearchOption) => Promise<void>;
  productDetails: ProductDetails | undefined;
  selectedProductType: SearchOption | null;
  setHighlightDates: Dispatch<SetStateAction<any>>;
  setSelectedProductType: Dispatch<SetStateAction<SearchOption | null>>;
};

export default function SmartCalendarMobileFooter({
  date,
  onProductTypeChange,
  handleConfirmClick,
  hotel,
  productDetails,
  selectedProductType,
  setHighlightDates,
  setSelectedProductType,
}: Props) {
  const { state } = useContext(store);
  const { smartCalendarProductFilter } = state;
  const { track } = useEvents();
  const { ctasUpdateExperiment } = useFeatureFlagContext();

  const productTypeOptions: SearchOption[] = useMemo(() => {
    if (!productDetails?.products) {
      return [];
    }

    return productDetails.products.map((product: Product) => ({
      label: product.name,
      value: product.product_time_group_id || product.product_id.toString(),
      hideOtherOptions: false,
    }));
  }, [productDetails]);

  const isOptionVisible = (option: string) => {
    if (smartCalendarProductFilter?.hideOtherOptions) {
      return smartCalendarProductFilter.value === option;
    }

    return true;
  };

  const onProductTypeOptionSelect = (selectedOption: SearchOption | 'all') => {
    track(SELECT_CALENDAR_PRODUCT_FILTER, {
      hotel_id: hotel.id || null,
      selected_date: date ? format(date, 'yyyy-MM-dd') : null,
      selected_filter: selectedOption === 'all' ? 'all' : selectedOption.label,
    });

    if (selectedOption === 'all') {
      setSelectedProductType(null);
      setHighlightDates([]);
      return;
    }

    setSelectedProductType(selectedOption);

    onProductTypeChange(selectedOption);

    // Event
    dlpSmartCalendarProductFilterApplied(hotel.metadata.hotel_id || null, selectedOption.label);
  };

  return (
    <div className="date-footer shadow-drawer-footer pb-4 pt-5 w-full absolute bottom-0 bg-white">
      <div className="flex flex-row items-center no-scrollbar overflow-x-auto w-full">
        <div className="flex-shrink-0 ml-4">
          <button
            type="button"
            onClick={() => {
              onProductTypeOptionSelect('all');
            }}
            className={`${
              selectedProductType === null
                ? 'text-white bg-rp-primary-black border-rp-primary-black'
                : 'border-rp-gray-divider'
            } border font-rp-primary-black font-rp-pn-regular font-semibold leading-17px px-2 py-1 rounded-md text-sm`}
          >
            All Products
          </button>
        </div>

        {productTypeOptions &&
          productTypeOptions.map((productTypeOption, index, { length }) => (
            <div
              key={productTypeOption.value}
              className={`${index + 1 === length ? 'mr-4' : ''} ml-2 flex-shrink-0 ${
                isOptionVisible(productTypeOption.value) ? '' : 'hidden'
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  onProductTypeOptionSelect(productTypeOption);
                }}
                className={`${
                  selectedProductType === productTypeOption
                    ? 'text-white bg-rp-primary-black border-rp-primary-black'
                    : 'border-rp-gray-divider'
                }
                border font-rp-primary-black font-rp-pn-regular font-semibold leading-17px px-2 py-1 rounded-md flex-shrink-0 text-sm`}
              >
                {productTypeOption.label}
              </button>
            </div>
          ))}
      </div>

      <div className="flex items-center justify-between mt-4 px-4">
        <div className="w-full">
          <p className="font-rp-pn-light text-sm">Date:</p>
          {date ? (
            <div className="text-sm d:font-semibold">
              {`${getMonthAndDay(date)}, ${date.getFullYear()}`}
            </div>
          ) : (
            <span>&#8212;</span>
          )}
        </div>

        <div className="w-3/5">
          {!ctasUpdateExperiment || ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_CONTROL_NAME ? (
            <ButtonPrimary onClick={handleConfirmClick} classes="h-12 text-base" isDisabled={!date}>
              Confirm
            </ButtonPrimary>
          ) : null}

          {ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
            <Button variant="primary" size="lg" onClick={handleConfirmClick} disabled={!date}>
              Confirm
            </Button>
          ) : null}

          {ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME_2 ? (
            <Button variant="ultramarine" size="lg" onClick={handleConfirmClick} disabled={!date}>
              Confirm
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
